import { useEffect, useState, useCallback } from 'react'
import { Key, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Scan {
  id: number
  repo: string
  source_tool: string
  findings_total: number
  findings_critical: number
  findings_high: number
  findings_medium: number
  findings_low: number
  branch: string
  commit_sha: string
  scanned_at: string
}

export default function SecretScansPage() {
  const [runs, setRuns] = useState<Scan[]>([])
  const [perRepo, setPerRepo] = useState<Scan[]>([])
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(90)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sec/secret-scans', { params: { days, limit: 200 } })
      setRuns(r.data?.runs || [])
      setPerRepo(r.data?.per_repo_latest || [])
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Trend: total findings per day across all repos
  const byDay: Record<string, number> = {}
  runs.forEach(r => {
    const d = (r.scanned_at || '').slice(0, 10)
    byDay[d] = (byDay[d] || 0) + r.findings_total
  })
  const trendDays = Object.entries(byDay).sort((a, b) => a[0].localeCompare(b[0]))
  const maxBar = Math.max(1, ...trendDays.map(([, n]) => n))

  const totals = perRepo.reduce((acc, r) => ({
    critical: acc.critical + r.findings_critical,
    high:     acc.high     + r.findings_high,
    medium:   acc.medium   + r.findings_medium,
    low:      acc.low      + r.findings_low,
    total:    acc.total    + r.findings_total,
  }), { critical: 0, high: 0, medium: 0, low: 0, total: 0 })

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Key size={24} className="text-amber-light" /> Secrets Sprawl
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Gitleaks / Trufflehog / detect-secrets scan history. Push from CI via <code className="font-mono">/api/sec/secret-scans</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} className="input py-1.5 text-xs">
            <option value={30}>30d</option><option value={90}>90d</option><option value={180}>180d</option>
          </select>
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Repos scanned" value={perRepo.length} tone="blue" />
        <Tile label="Total findings (latest)" value={totals.total} tone="amber" />
        <Tile label="Critical" value={totals.critical} tone="rose" pulse={totals.critical > 0} />
        <Tile label="High" value={totals.high} tone="amber" pulse={totals.high > 0} />
        <Tile label="Medium / low" value={totals.medium + totals.low} tone="blue" />
      </div>

      {trendDays.length > 0 && (
        <div className="card p-3">
          <h3 className="text-xs font-semibold text-text-primary mb-2">Findings per day (all repos)</h3>
          <div className="flex items-end gap-1" style={{ height: 80 }}>
            {trendDays.map(([d, n]) => (
              <div key={d} className="flex-1 flex flex-col items-center justify-end" title={`${d}: ${n}`}>
                <div className="w-full bg-amber/40 rounded-t" style={{ height: `${(n / maxBar) * 100}%`, minHeight: 1 }} />
                <span className="text-[8px] text-text-muted mt-0.5">{d.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        <h3 className="text-xs font-semibold text-text-primary mb-2">Latest scan per repo</h3>
        {perRepo.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">
            No scans recorded. CI example: <code className="font-mono">curl -X POST $API/api/sec/secret-scans -d '{`{"repo":"foo","findings_total":3,"findings_high":1}`}'</code>
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Repo</th>
                <th className="text-left py-2 px-2">Tool</th>
                <th className="text-right py-2 px-2">Total</th>
                <th className="text-right py-2 px-2">Crit</th>
                <th className="text-right py-2 px-2">High</th>
                <th className="text-right py-2 px-2">Med</th>
                <th className="text-right py-2 px-2">Low</th>
                <th className="text-left py-2 px-2">Last scan</th>
              </tr>
            </thead>
            <tbody>
              {perRepo.map(r => (
                <tr key={r.repo} className={clsx('border-b border-border/50',
                  r.findings_critical > 0 && 'bg-rose/5')}>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-blue-light">{r.repo}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{r.source_tool}</td>
                  <td className="py-1.5 px-2 text-right font-mono font-bold text-text-primary">{r.findings_total}</td>
                  <td className={clsx('py-1.5 px-2 text-right font-mono', r.findings_critical > 0 ? 'text-rose font-bold' : 'text-text-muted')}>{r.findings_critical}</td>
                  <td className={clsx('py-1.5 px-2 text-right font-mono', r.findings_high > 0 ? 'text-amber-light font-bold' : 'text-text-muted')}>{r.findings_high}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-text-muted">{r.findings_medium}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-text-muted">{r.findings_low}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{r.scanned_at?.slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
